/*
 * IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: Moser
 *
 * (C) Copyright IBM Corp. 2017, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
*/

import ObjectFactory from '../generated/ObjectFactory';
import JSONObjectFactory from '../generated/JSONObjectFactory';
import Features from '../generated/Features';
import Notification from '../generated/Notification';
import MoserObject from '../generated/MoserObject';
import MoserObjectTypes from '../generated/MoserObjectTypes';
import ModuleUtils from '../generated/ModuleUtils';
import InsertUtils from '../generated/InsertUtils';
import InsertRecord from '../generated/InsertRecord';
import InsertFeedback from '../generated/InsertFeedback';
import MetricDefinitionHierarchyItem from '../generated/MetricDefinitionHierarchyItem';
import MetricDefinitionHierarchy from '../generated/MetricDefinitionHierarchy';
import MetricUtils from '../generated/MetricUtils'
import ObjectNavigator from '../generated/ObjectNavigator';
import EmbeddedModuleUtils from '../generated/EmbeddedModuleUtils';
import IdGenerator from '../generated/IdGenerator';
import ValidationUtils from '../generated/ValidationUtils';
import BinExpression from '../generated/BinExpression';
import QueryFlowUtil from '../generated/QueryFlowUtil';
import CDEConverterUtil from '../generated/CDEConverterUtil';
import Source from '../generated/Source';
import FileSource from '../generated/FileSource';
import DatabaseSource from '../generated/DatabaseSource';
import GenericClientUtils from '../generated/GenericClientUtils';
import WhatIfModelUtil from '../generated/WhatIfModelUtil';
import ShortcutUtils from '../generated/ShortcutUtils';
import ConversionUtils from '../generated/ConversionUtils';
import EditModeUtils from '../generated/EditModeUtils';
import DynamicSchemaUtils from '../generated/DynamicSchemaUtils';
import JoinGraph from '../generated/JoinGraph';
import MetricJoinGraph from '../generated/MetricJoinGraph';
import BinExpressionUtil from '../generated/BinExpressionUtil';
import CommonResolverUtil from '../generated/CommonResolverUtil';
// Enums

import ParameterDataType from '../generated/ParameterDataType';
import ValueOperatorType from '../generated/ValueOperatorType';
import CardinalityEnum from '../generated/CardinalityEnum';
import InstanceType from '../generated/InstanceType';
import RegularAggregateType from '../generated/RegularAggregateType';
import SourceCategoryType from '../generated/SourceCategoryType';
import UsageType from '../generated/UsageType';
import AggregateMode from '../generated/AggregateMode';
import ContextMode from '../generated/ContextMode';
import FacetTypeEnabled from '../generated/FacetTypeEnabled';
import GenerateSQL from '../generated/GenerateSQL';
import HighLevelDataType from '../generated/HighLevelDataType';
import JoinFilterType from '../generated/JoinFilterType';
import ProParameterMode from '../generated/ProParameterMode';
import QuerySubjectUsage from '../generated/QuerySubjectUsage';
import QuerySubjectUtils from '../generated/QuerySubjectUtils';
import SetOperation from '../generated/SetOperation';
import SetOperationDuplicates from '../generated/SetOperationDuplicates';
import SortOrder from '../generated/SortOrder';
import SortOrderNullPlacement from '../generated/SortOrderNullPlacement';
import SplitColumnTreatment from '../generated/SplitColumnTreatment';
import SplitDisplay from '../generated/SplitDisplay';
import SqlLanguageType from '../generated/SqlLanguageType';
import TableType from '../generated/TableType';
import UseSpecType from '../generated/UseSpecType';
import QsClassifierType from '../generated/QsClassifierType';
import KeyCompositionType from '../generated/KeyCompositionType';
import KeyConstraintType from '../generated/KeyConstraintType';
import SqlOperatorType from '../generated/SqlOperatorType';
import CopyMode from '../generated/CopyMode';
import ComparisonOperatorType from '../generated/ComparisonOperatorType';
import SelectListControlType from '../generated/SelectListControlType';
import Operator from '../generated/Operator';
import CalculatedMemberTypeEnum from '../generated/CalculatedMemberTypeEnum';
import EditModeType from '../generated/EditModeType';
import ConvertQSType from '../generated/ConvertQSType';
import InsertStatus from '../generated/InsertStatus';
import ActivationMode from '../generated/ActivationMode';
import CaseSensitivityMode from '../generated/CaseSensitivityMode';
import ValueSearchType from '../generated/ValueSearchType';
import TransientSourceType from '../generated/TransientSourceType';
import MetricRoleType from '../generated/MetricRoleType';
import SentimentType from '../generated/SentimentType';
import PerspectiveType from '../generated/PerspectiveType';

class EntryPoint {
	
	static INSTANCE = new EntryPoint();

	Notification = { 
		ADD: Notification.ADD,
		REMOVE: Notification.REMOVE,
		SET: Notification.SET,
		CREATE: Notification.CREATE,
		NO_POSITION: Notification.NO_POSITION
		};
	
	Features = Features;
	MoserObjectTypes = MoserObjectTypes;	
	ModuleUtils = ModuleUtils;
	InsertUtils = InsertUtils;
	InsertRecord = InsertRecord;
	InsertFeedback = InsertFeedback;
	MetricDefinitionHierarchy = MetricDefinitionHierarchy;
	MetricDefinitionHierarchyItem = MetricDefinitionHierarchyItem;
	MetricUtils = MetricUtils;
	ObjectNavigator = ObjectNavigator;
	EmbeddedModuleUtils = EmbeddedModuleUtils;
	IdGenerator = IdGenerator;
	ValidationUtils = ValidationUtils;	
	BinExpression = BinExpression;
	QueryFlowUtil = new QueryFlowUtil();
	WhatIfModelUtil = new WhatIfModelUtil();
	ShortcutUtils = ShortcutUtils;
	ConversionUtils = ConversionUtils;
	EditModeUtils = EditModeUtils;
	DynamicSchemaUtils = DynamicSchemaUtils
	CDEConverterUtil = new CDEConverterUtil();
	Source  = Source;
	FileSource = FileSource;
	DatabaseSource = DatabaseSource;
	JoinGraph = JoinGraph;
	MetricJoinGraph = MetricJoinGraph;
	BinExpressionUtil = BinExpressionUtil;
	CommonResolverUtil = CommonResolverUtil;

	// Enums
	ParameterDataType = ParameterDataType;
	ValueOperatorType = ValueOperatorType;
	Operator = Operator;
	CardinalityEnum = CardinalityEnum;
	InstanceType = InstanceType;
	RegularAggregateType = RegularAggregateType;
	SourceCategoryType = SourceCategoryType;
	UsageType = UsageType;	
	AggregateMode = AggregateMode;
	ContextMode = ContextMode;
	FacetTypeEnabled = FacetTypeEnabled;
	GenerateSQL = GenerateSQL;
	HighLevelDataType = HighLevelDataType;
	JoinFilterType = JoinFilterType;
	ProParameterMode = ProParameterMode;
	QuerySubjectUsage = QuerySubjectUsage;
	QuerySubjectUtils = QuerySubjectUtils;
	SetOperation = SetOperation;
	SetOperationDuplicates = SetOperationDuplicates;
	SortOrder = SortOrder;
	SortOrderNullPlacement = SortOrderNullPlacement;
	SplitColumnTreatment = SplitColumnTreatment;
	SplitDisplay = SplitDisplay;
	SqlLanguageType = SqlLanguageType;
	TableType = TableType;
	UseSpecType = UseSpecType;
	QsClassifierType = QsClassifierType;
	KeyCompositionType = KeyCompositionType;
	KeyConstraintType = KeyConstraintType;
	SqlOperatorType = SqlOperatorType;
	CopyMode = CopyMode;
	ComparisonOperatorType = ComparisonOperatorType;
	SelectListControlType = SelectListControlType;
	EditModeType = EditModeType;
	ConvertQSType = ConvertQSType;
	InsertStatus = InsertStatus;
	ActivationMode = ActivationMode;
	CaseSensitivityMode = CaseSensitivityMode;
	ValueSearchType = ValueSearchType;
	TransientSourceType = TransientSourceType;
	MetricRoleType = MetricRoleType;
	SentimentType = SentimentType;
	PerspectiveType = PerspectiveType;
	
	createObjectFactory() {
		return new ObjectFactory();
	}
	
	createJSONObjectFactory(objectFactory) {
		return new JSONObjectFactory(objectFactory);
	}
	
	isMoserObject(object) {
		return (object instanceof MoserObject);
	}
}

export default EntryPoint.INSTANCE;


// WEBPACK FOOTER //
// ./src/custom/EntryPoint.js